import { useEffect, useRef, useState } from 'react';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { InlineLoadingIndicator } from '@/components/ui/inline-loading';

export interface KycEmbedProps {
  /**
   * URL to embed (ToS or KYC link from Bridge)
   */
  readonly url: string;

  /**
   * List of allowed hostnames for security
   * Example: ['bridge.xyz', 'withpersona.com']
   */
  readonly allowedHosts: string[];

  /**
   * Callback when user completes the flow in iframe
   * Triggered by postMessage from Bridge
   */
  readonly onComplete?: () => void;

  /**
   * Callback when iframe fails to load or is blocked
   */
  readonly onError?: (error: string) => void;

  /**
   * Title for the embed section
   */
  readonly title?: string;

  /**
   * Description text shown above iframe
   */
  readonly description?: string;

  /**
   * Show fallback link to open in new window
   */
  readonly showFallback?: boolean;
}

function isHostAllowed(url: string, allowedHosts: string[]): boolean {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    return allowedHosts.some(allowedHost => {
      const normalizedHost = allowedHost.toLowerCase();
      return hostname === normalizedHost || hostname.endsWith(`.${normalizedHost}`);
    });
  } catch {
    return false;
  }
}

export function KycEmbed({
  url,
  allowedHosts,
  onComplete,
  onError,
  title = 'Complete Verification',
  description,
  showFallback = true,
}: KycEmbedProps) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [iframeError, setIframeError] = useState<string | null>(null);
  const [iframeBlocked, setIframeBlocked] = useState(false);

  // Validate URL against allowlist
  const urlSafe = isHostAllowed(url, allowedHosts);

  useEffect(() => {
    if (!urlSafe) {
      const errorMessage = 'URL not in allowed hosts list';
      setIframeError(errorMessage);
      onError?.(errorMessage);
      return;
    }

    // Listen for postMessage from Bridge iframe
    const handleMessage = (event: MessageEvent) => {
      // Verify origin is allowed
      const originAllowed = allowedHosts.some(host => {
        const normalized = host.toLowerCase();
        return event.origin.toLowerCase().includes(normalized);
      });

      if (!originAllowed) {
        console.warn('[KycEmbed] Ignored message from untrusted origin:', event.origin);
        return;
      }

      console.log('[KycEmbed] Received message:', { origin: event.origin, data: event.data });
      console.log('[KycEmbed] 🔍 Full event.data:', JSON.stringify(event.data, null, 2));

      // Bridge sends success message on completion
      // Common patterns: { type: 'success' }, { event: 'complete' }, { status: 'approved' }
      if (event.data) {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        console.log('[KycEmbed] 📊 Parsed data:', JSON.stringify(data, null, 2));
        console.log('[KycEmbed] 🔍 Checking all fields:', {
          'data.type': data.type,
          'data.event': data.event,
          'data.status': data.status,
          'data.eventName': data.eventName,
          'data.name': data.name,
          'data.action': data.action,
          'data.verification_status': data.verification_status,
          'data.signedAgreementId': data.signedAgreementId,
        });

        // EXPANDED SUCCESS DETECTION - covers all known Bridge/Persona postMessage formats
        if (
          // Type field variations
          data.type === 'success' ||
          data.type === 'complete' ||
          data.type === 'done' ||
          // Event field variations
          data.event === 'complete' ||
          data.event === 'done' ||
          data.event === 'success' ||
          // Status field variations
          data.status === 'approved' ||
          data.status === 'accepted' ||
          data.status === 'complete' ||
          data.status === 'completed' ||
          // EventName field variations
          data.eventName === 'done' ||
          data.eventName === 'complete' ||
          data.eventName === 'success' ||
          data.eventName === 'tos_accepted' ||
          data.eventName === 'kyc_complete' ||
          // Name field variations
          data.name === 'done' ||
          data.name === 'complete' ||
          data.name === 'success' ||
          // Action field variations
          data.action === 'complete' ||
          data.action === 'done' ||
          // Verification status field
          data.verification_status === 'approved' ||
          data.verification_status === 'completed' ||
          // Bridge ToS specific signal
          data.signedAgreementId
        ) {
          console.log('[KycEmbed] ✅ Flow completed successfully - condition matched!');
          onComplete?.();
        } else {
          console.warn('[KycEmbed] ⚠️ Message received but no condition matched.');
          console.warn('[KycEmbed] 📋 Full unmatched data dump:', data);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [url, allowedHosts, urlSafe, onComplete, onError]);

  // Handle iframe load
  const handleIframeLoad = () => {
    setLoading(false);
    console.log('[KycEmbed] Iframe loaded successfully');
  };

  // Handle iframe error
  const handleIframeError = () => {
    const errorMessage = 'Failed to load verification page';
    setIframeError(errorMessage);
    setLoading(false);
    onError?.(errorMessage);
    console.error('[KycEmbed] Iframe failed to load');
  };

  // Detect if iframe is blocked (timeout after 5s)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading && !iframeError) {
        setIframeBlocked(true);
        setLoading(false);
        onError?.('Verification page may be blocked. Please allow popups and iframes.');
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [loading, iframeError, onError]);

  const handleOpenExternal = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (!urlSafe) {
    return (
      <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-6">
        <div className="flex items-start gap-4">
          <SFIcon icon={sfIcons.error} className="h-6 w-6 flex-shrink-0 text-destructive" />
          <div className="flex-1 space-y-2">
            <p className="font-medium text-red-900">Security Error</p>
            <p className="text-sm text-destructive">
              The verification URL is not from a trusted source. Please contact support.
            </p>
            <div className="text-xs text-destructive font-mono">{url}</div>
          </div>
        </div>
      </div>
    );
  }

  if (iframeError && !iframeBlocked) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6">
        <div className="flex items-start gap-4">
          <SFIcon icon={sfIcons.warning} className="h-6 w-6 flex-shrink-0 text-amber-600" />
          <div className="flex-1 space-y-3">
            <div>
              <p className="font-medium text-amber-900">Verification Page Unavailable</p>
              <p className="text-sm text-amber-800 mt-1">{iframeError}</p>
            </div>
            {showFallback && (
              <Button onClick={handleOpenExternal} variant="outline" size="sm" className="gap-2">
                <SFIcon icon={sfIcons.externalLink} className="h-4 w-4" />
                Open in New Window
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (iframeBlocked) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6">
        <div className="flex items-start gap-4">
          <SFIcon icon={sfIcons.warning} className="h-6 w-6 flex-shrink-0 text-amber-600" />
          <div className="flex-1 space-y-3">
            <div>
              <p className="font-medium text-amber-900">Verification Page Blocked</p>
              <p className="text-sm text-amber-800 mt-1">
                Your browser may be blocking embedded verification pages. Please open the verification in a new window.
              </p>
            </div>
            <Button onClick={handleOpenExternal} variant="default" size="sm" className="gap-2">
              <SFIcon icon={sfIcons.externalLink} className="h-4 w-4" />
              Open Verification
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {(title || description) && (
        <div className="space-y-2">
          {title && <h2 className="font-display text-heading-md">{title}</h2>}
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
      )}

      <div className="relative rounded-lg border border-border bg-background overflow-hidden">
        {loading && (
          <div className={cn(
            "absolute inset-0 z-10 flex items-center justify-center",
            // Liquid Glass Surface tier overlay
            "bg-gradient-to-br from-background/85 via-background/75 to-background/85",
            "backdrop-blur-lg backdrop-saturate-[180%]"
          )}>
            <div className="flex flex-col items-center gap-3">
              <InlineLoadingIndicator
                size="lg"
                aria-label="Loading verification"
              />
              <p className="text-sm text-muted-foreground">Loading verification...</p>
            </div>
          </div>
        )}

        <iframe
          ref={iframeRef}
          src={url}
          title={title}
          className="w-full h-[600px] border-0"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation allow-top-navigation-by-user-activation allow-modals allow-downloads"
          allow="camera *; microphone *; geolocation *; document-domain *; fullscreen *; display-capture *; payment *"
        />
      </div>

      {showFallback && !loading && (
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <span>Having trouble?</span>
          <button
            onClick={handleOpenExternal}
            className="text-primary hover:underline inline-flex items-center gap-1"
          >
            Open in new window
            <SFIcon icon={sfIcons.externalLink} className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}
